import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DocumentType } from "constants/adminApi";
import { isPdfDocument } from "utils/kycDocument";
import { PreparedAuditLogEntry, isApiLogFailure } from "./apiLogDisplayUtils";
import NameValuePair from "components/pages/KYCsteps/confirmDetails/nameValuePair";
import ApiLogParamsSection from "./ApiLogParamsSection";
import HttpStatusChip from "./HttpStatusChip";
import PanComplianceIndicator from "./PanComplianceIndicator";
import {
  VerificationOutcomeChip,
  apiLogToVerificationStep,
  documentTypeForRequestType,
  formatDate,
  formatRequestTypeLabel,
  resolveApiLogDocumentKey,
} from "./adminKycVerificationUtils";
import { KycDocumentLoadState } from "../hooks/useKycDocumentLoader";

interface ApiLogTimelineEntryProps {
  entry: PreparedAuditLogEntry;
  getDocumentState: (documentType: DocumentType, objectKey?: string) => KycDocumentLoadState;
  loadDocument: (
    documentType: DocumentType,
    objectKey?: string,
    force?: boolean
  ) => Promise<string | null>;
  onPreviewDocument: (documentType: DocumentType, objectKey: string, title: string) => void;
  onDownloadDocument: (documentType: DocumentType, objectKey: string) => void;
}

function ApiLogInlinePreview({
  documentType,
  objectKey,
  state,
  onExpand,
  onRetry,
  compact = false,
}: {
  documentType: DocumentType;
  objectKey: string;
  state: KycDocumentLoadState;
  onExpand: () => void;
  onRetry: () => void;
  compact?: boolean;
}) {
  if (state.loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Loading document preview...
        </Typography>
      </Stack>
    );
  }

  if (state.error) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="error.main">
          {state.error}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (!state.url) {
    return null;
  }

  const isPdf = isPdfDocument(objectKey);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Expand document preview"
      onClick={onExpand}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onExpand();
        }
      }}
      sx={{
        height: compact ? "100%" : 220,
        minHeight: compact ? 280 : 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f0f2f5",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        cursor: "zoom-in",
      }}
    >
      {isPdf ? (
        <Box
          component="iframe"
          src={state.url}
          title={`${documentType} preview`}
          sx={{ width: "100%", height: "100%", border: 0, bgcolor: "#fff" }}
        />
      ) : (
        <Box
          component="img"
          src={state.url}
          alt={`${documentType} preview`}
          sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      )}
    </Box>
  );
}

function ApiLogTimelineEntry({
  entry,
  getDocumentState,
  loadDocument,
  onPreviewDocument,
  onDownloadDocument,
}: ApiLogTimelineEntryProps) {
  const [expanded, setExpanded] = useState(false);
  const documentKey = resolveApiLogDocumentKey(entry);
  const documentType = documentTypeForRequestType(entry.requestType);
  const hasDocument = Boolean(documentKey && documentType);
  const verificationStep = apiLogToVerificationStep(entry);
  const revealSensitive = isApiLogFailure(entry);
  const previewTitle = documentType
    ? `${formatRequestTypeLabel(entry.requestType)} — ${documentType === "PAN_CARD_PROOF" ? "PAN Document" : "Bank Document"}`
    : "Document";

  const docState =
    hasDocument && documentType
      ? getDocumentState(documentType, documentKey)
      : { url: null, loading: false, error: null };

  useEffect(() => {
    if (expanded && hasDocument && documentType && documentKey) {
      loadDocument(documentType, documentKey);
    }
  }, [expanded, hasDocument, documentType, documentKey, loadDocument]);

  const handlePreview = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (documentType && documentKey) {
      onPreviewDocument(documentType, documentKey, previewTitle);
    }
  };

  const handleDownload = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (documentType && documentKey) {
      onDownloadDocument(documentType, documentKey);
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{ border: "1px solid", borderColor: "divider" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ width: "100%", pr: 1 }}
        >
          <Typography variant="body2" fontWeight={600}>
            {formatRequestTypeLabel(entry.requestType)}
          </Typography>
          <VerificationOutcomeChip step={verificationStep} />
          <HttpStatusChip status={entry.status} />
          {entry.requestType === "PAN_VERIFICATION" && (
            <PanComplianceIndicator complianceLog={entry.panComplianceLog} />
          )}
          {hasDocument && (
            <Chip
              size="small"
              variant="outlined"
              icon={<InsertDriveFileOutlinedIcon />}
              label="Document"
              sx={{ height: 24 }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
          >
            {formatDate(entry.createdOn)}
          </Typography>
          {hasDocument && (
            <Stack direction="row" spacing={0.5} onClick={(event) => event.stopPropagation()}>
              <Tooltip title="View document">
                <IconButton
                  size="small"
                  aria-label="View document for this API log entry"
                  onClick={handlePreview}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download document">
                <IconButton
                  size="small"
                  aria-label="Download document for this API log entry"
                  onClick={handleDownload}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {entry.message && (
            <NameValuePair name="Summary" value={entry.message} emphasizeValue />
          )}

          <Grid container spacing={2.5} alignItems="stretch">
            {hasDocument && documentType && documentKey && (
              <Grid item xs={12} md={5}>
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Attached document
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" startIcon={<FullscreenIcon />} onClick={handlePreview}>
                        Expand
                      </Button>
                      <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                        Download
                      </Button>
                    </Stack>
                  </Stack>
                  <Box sx={{ flex: 1 }}>
                    <ApiLogInlinePreview
                      documentType={documentType}
                      objectKey={documentKey}
                      state={docState}
                      onExpand={handlePreview}
                      onRetry={() => loadDocument(documentType, documentKey, true)}
                      compact
                    />
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={hasDocument ? 7 : 12}>
              <Stack spacing={2} sx={{ height: "100%" }}>
                <ApiLogParamsSection
                  title="What was sent"
                  params={entry.requestParams}
                  revealSensitive={revealSensitive}
                />
                <ApiLogParamsSection
                  title="What was received"
                  params={entry.responseParams}
                  revealSensitive={revealSensitive}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default ApiLogTimelineEntry;
